import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { Button } from '@column/column-ui-kit';
import chroma from 'chroma-js';
import { getPlatformStatus } from './PlatformBanner';
import { ROUTE as APP_ROUTE } from '~/app/routes';
import { ROUTE } from '~/public/routes';
import { useSessionStore } from '~/stores/Session';

interface SandboxBannerProps {}

const SandboxBannerAppear = keyframes`
  to {
    transform: translateY(0);
  }
`;

const SandboxBannerDisappear = keyframes`
  to {
    transform: translateY(-100%);
  }
`;

const Wrapper = styled.div<{ $isShow?: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 2;
  padding: 8px;
  text-align: center;
  line-height: 20px;
  font-size: 14px;
  font-weight: 600;
  height: 36px;
  box-sizing: border-box;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  color: ${({ theme }) => theme.primary.foreground};
  background-image: linear-gradient(
    40deg,
    ${({ theme }) => theme.primary.blendToBackground(1050)},
    ${({ theme }) => chroma(theme.primary.blendToBackground(1200)).saturate(-0.5).hex()} 60%
  );
  box-shadow: inset 0 -1px 0 0 ${({ theme }) => theme.primary.blendToBackground(1200)},
    inset 0 -4px 8px 0 rgba(38 38 44 / 10%);
  transform: translateY(-100%);
  animation: ${({ $isShow }) => ($isShow ? SandboxBannerAppear : SandboxBannerDisappear)} 0.15s ease forwards;
`;

const SandboxBannerButton = styled(Button)`
  display: inline-block;
  vertical-align: top;
  margin: -2px 0 -2px 4px;
  color: ${({ theme }) => theme.primary.blendToBackground(1200)};

  &:after {
    inset: 0;
    background-color: rgba(255, 255, 255, 1);
  }
  &:hover:after {
    background-color: rgba(255, 255, 255, 0.9);
  }
`;

export const SandboxBanner: React.FC<SandboxBannerProps> = () => {
  const navigate = useNavigate();
  const showSandboxBanner = useSessionStore((s) => s.showSandboxBanner);
  const currentPlatform = useSessionStore((s) => s.currentPlatform);

  const platformStatus = getPlatformStatus(currentPlatform?.platformLevel);
  const onboardingEnabled = currentPlatform?.metadata?.defaultDependencyGraphInstanceId;

  return (
    <Wrapper $isShow={showSandboxBanner}>
      {platformStatus === 'approved'
        ? 'You’re in sandbox mode which is used for testing. To move real money turn sandbox off in the bottom left corner.'
        : platformStatus === 'pending'
        ? 'Your onboarding approval is pending. We will contact you with updates on your account approval status.'
        : onboardingEnabled
        ? 'Please finish our onboarding process for us to review. '
        : 'Please contact us to start your onboarding process. '}
      {platformStatus !== 'approved' && platformStatus !== 'pending' && onboardingEnabled && (
        <SandboxBannerButton size="tiny" variant="muted" onClick={() => navigate(ROUTE.REGISTER_BUSINESS_INFORMATION)}>
          Start
        </SandboxBannerButton>
      )}
      {platformStatus !== 'approved' && platformStatus !== 'pending' && !onboardingEnabled && (
        <SandboxBannerButton size="tiny" variant="muted" onClick={() => navigate(APP_ROUTE.CONTACT_FORM)}>
          Contact Us
        </SandboxBannerButton>
      )}
    </Wrapper>
  );
};
